(function () {
  const brandSearchSelector = '[data-tikoo-brand-search]';
  const brandInputSelector = '[data-tikoo-brand-search-input]';
  const brandStatusSelector = '[data-tikoo-brand-search-status]';
  const brandValuesSelector = '[data-tikoo-brand-values]';
  const facetValuesSelector = '[data-tikoo-facet-values]';
  const facetInputSelector = '[data-tikoo-facet-input]';
  const singleSelectSelector = '[data-tikoo-single-select]';
  const vintageCategoryValuesSelector = '[data-tikoo-vintage-category-values]';
  const vintageDetailValuesSelector = '[data-tikoo-vintage-detail-values]';
  const categoryToggleSelector = '[data-tikoo-category-toggle]';
  const vintageCategoryParamName = 'filter.p.m.vintage.category';
  const mobileViewportQuery = '(max-width: 989px)';
  const mobileFilterBarSelector = '[data-tikoo-mobile-filter-bar]';
  const mobileFilterOpenSelector = '[data-tikoo-mobile-filter-open]';
  const mobileFilterCloseSelector = '[data-tikoo-mobile-filter-close]';
  const mobileFilterCountSelector = '[data-tikoo-mobile-filter-count]';
  const remoteBrandLimit = 60;
  const defaultValueLimit = 6;
  const brandSearchDebounceMs = 250;
  let generatedCategorySequence = 0;

  function normalizeText(value) {
    if (typeof value !== 'string') return '';
    return value.trim();
  }

  function getConfig() {
    return window.TikooStorefrontConfig || {};
  }

  function getApiBaseUrl() {
    const config = getConfig();
    const proxyBasePath = normalizeText(config.proxyBasePath);
    const apiBaseUrl = normalizeText(config.apiBaseUrl);
    return (proxyBasePath || apiBaseUrl).replace(/\/+$/, '');
  }

  function isAppProxyBaseUrl(apiBaseUrl) {
    return apiBaseUrl.startsWith('/apps/');
  }

  function buildApiFetchUrl(apiBaseUrl, path) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    if (isAppProxyBaseUrl(apiBaseUrl) && normalizedPath.startsWith('/api/shopify/')) {
      return `${apiBaseUrl}${normalizedPath.replace('/api/shopify', '')}`;
    }

    return `${apiBaseUrl}${normalizedPath}`;
  }

  async function apiFetch(path) {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) {
      throw new Error('Tikoo App Proxy is not configured.');
    }

    const response = await fetch(buildApiFetchUrl(apiBaseUrl, path), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    const text = await response.text();
    const body = text ? JSON.parse(text) : null;
    if (!response.ok) {
      throw new Error(body?.message || `Request failed with status ${response.status}.`);
    }

    return body;
  }

  function getCollectionContext(root) {
    return {
      collection: normalizeText(root?.dataset.tikooCollectionHandle),
      profile: normalizeText(root?.dataset.tikooFilterProfile),
    };
  }

  function getCurrentSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  function getFilterParamsExceptBrand() {
    const params = getCurrentSearchParams();
    const result = new URLSearchParams();

    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (key === 'filter.p.m.vintage.brand' || key === 'filter.p.m.y2k_modern.brand') return;
      result.append(key, value);
    });

    return result;
  }

  function getBrandParamName(root) {
    const profile = getCollectionContext(root).profile;
    return 'filter.p.m.y2k_modern.brand';
  }

  function buildBrandSearchPath(root, query) {
    const context = getCollectionContext(root);
    const params = getFilterParamsExceptBrand();
    params.set('collection', context.collection);
    params.set('profile', context.profile);
    params.set('q', query);
    params.set('limit', String(remoteBrandLimit));
    return `/api/shopify/filters/brands?${params.toString()}`;
  }

  function updateUrlWithBrand(root, brandValue) {
    const params = getCurrentSearchParams();
    const brandParamName = getBrandParamName(root);
    const existingValues = params.getAll(brandParamName);
    if (!existingValues.some((item) => item.toLowerCase() === brandValue.toLowerCase())) {
      params.append(brandParamName, brandValue);
    }

    const searchParams = params.toString();
    if (window.FacetFiltersForm?.renderPage) {
      window.FacetFiltersForm.renderPage(searchParams);
      return;
    }

    window.location.search = searchParams;
  }

  function getBrandList(search) {
    return search.closest('.tikoo-facet-group')?.querySelector(brandValuesSelector);
  }

  function getStaticBrandItems(search) {
    const list = getBrandList(search);
    if (!list) return [];
    return [...list.querySelectorAll('[data-tikoo-facet-option]')];
  }

  function getBrandItemLabel(item) {
    return normalizeText(item.dataset.tikooFacetLabel || item.textContent);
  }

  function getFacetOptionLabel(item) {
    return normalizeText(item?.dataset.tikooFacetLabel || item?.textContent);
  }

  function isOtherFacetLabel(value) {
    return normalizeText(value).toLowerCase() === 'other';
  }

  function normalizeFacetKey(value) {
    return normalizeText(value).toLowerCase();
  }

  function setDetailItemDepth(item, depth) {
    item.style.setProperty('--tikoo-facet-depth', String(Math.max(depth, 0)));
  }

  function getCategoryChildItems(categoryList, parentKey) {
    if (!categoryList || !parentKey) return [];
    return getFacetItems(categoryList).filter((item) => item.dataset.tikooVintageDetailParentKey === parentKey);
  }

  function setCategoryExpanded(categoryList, parentItem, expanded) {
    if (!categoryList || !parentItem) return;

    const parentKey = parentItem.dataset.tikooVintageCategoryKey;
    parentItem.dataset.tikooCategoryExpanded = expanded ? 'true' : 'false';
    parentItem.classList.toggle('is-expanded', expanded);

    const toggle = parentItem.querySelector(categoryToggleSelector);
    if (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    getCategoryChildItems(categoryList, parentKey).forEach((item) => {
      item.dataset.tikooTreeCollapsed = expanded ? 'false' : 'true';
    });
  }

  function ensureCategoryToggle(categoryList, parentItem, label) {
    if (!categoryList || !parentItem) return;

    parentItem.querySelectorAll(categoryToggleSelector).forEach((button) => button.remove());
    const parentKey = parentItem.dataset.tikooVintageCategoryKey;
    if (getCategoryChildItems(categoryList, parentKey).length === 0) return;

    parentItem.classList.add('tikoo-facet-values__item--has-children');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tikoo-facet-category-toggle';
    button.dataset.tikooCategoryToggle = 'true';
    button.setAttribute('aria-label', `Toggle ${label} subcategories`);
    button.setAttribute('aria-expanded', parentItem.dataset.tikooCategoryExpanded === 'true' ? 'true' : 'false');
    parentItem.append(button);

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      setCategoryExpanded(categoryList, parentItem, parentItem.dataset.tikooCategoryExpanded !== 'true');
      applyFacetListLimit(categoryList);
    });
  }

  function resetCategoryParentItem(item) {
    item.classList.remove('has-active-child', 'is-expanded', 'tikoo-facet-values__item--has-children');
    item.querySelectorAll(categoryToggleSelector).forEach((button) => button.remove());
    delete item.dataset.tikooCategoryExpanded;
    delete item.dataset.tikooVintageCategoryKey;
  }

  function restoreMergedDetailItems(categoryList, detailList) {
    // 每次重新初始化前先把已移动的子类目放回隐藏列表，避免重复初始化破坏父子关系。
    categoryList.querySelectorAll('.tikoo-facet-values__item--child').forEach((item) => {
      item.classList.remove('tikoo-facet-values__item--child');
      item.style.removeProperty('--tikoo-facet-depth');
      delete item.dataset.tikooTreeCollapsed;
      delete item.dataset.tikooVintageDetailParentKey;
      detailList.append(item);
    });

    categoryList
      .querySelectorAll('.tikoo-facet-values__item--generated-parent')
      .forEach((item) => item.remove());
  }

  function getFacetVisibleLimit(list) {
    const parsedLimit = Number.parseInt(list?.dataset.tikooValueLimit || '', 10);
    return Number.isFinite(parsedLimit) && parsedLimit > 0 ? parsedLimit : defaultValueLimit;
  }

  function getFacetItems(list) {
    if (!list) return [];
    return [...list.querySelectorAll('[data-tikoo-facet-option], [data-tikoo-remote-brand-option]')];
  }

  function isMobileViewport() {
    return window.matchMedia?.(mobileViewportQuery)?.matches === true;
  }

  function getFacetGroups(root) {
    return [...root.querySelectorAll('.tikoo-facet-group')];
  }

  function getMobileFilterTargetParam(group) {
    return normalizeText(group?.dataset.tikooMobileFilterTarget || group?.dataset.tikooFilterParam);
  }

  function isMobileSkippedGroup(group) {
    return group?.hasAttribute('data-tikoo-mobile-filter-skip') === true;
  }

  function isMergedHiddenFacetGroup(group) {
    return group?.hasAttribute('data-tikoo-merge-into-category') === true
      || group?.dataset.tikooMergedIntoCategory === 'true';
  }

  function hasCheckedFacetInput(group) {
    return !!group?.querySelector(`${facetInputSelector}:checked`);
  }

  function setMobileFilterButtonState(root) {
    const bar = root.querySelector(mobileFilterBarSelector);
    if (!bar) return;

    const activeCount = getFacetGroups(root)
      .filter((group) => !isMobileSkippedGroup(group))
      .reduce((total, group) => total + group.querySelectorAll(`${facetInputSelector}:checked`).length, 0);
    const totalButton = bar.querySelector(`${mobileFilterOpenSelector}:not([data-tikoo-mobile-filter-param])`);
    const countElement = bar.querySelector(mobileFilterCountSelector);
    if (totalButton) {
      totalButton.classList.toggle('is-active', activeCount > 0);
    }
    if (countElement) {
      countElement.textContent = String(activeCount);
      countElement.hidden = activeCount === 0;
    }

    bar.querySelectorAll(`${mobileFilterOpenSelector}[data-tikoo-mobile-filter-param]`).forEach((button) => {
      const param = normalizeText(button.dataset.tikooMobileFilterParam);
      const hasActiveGroup = getFacetGroups(root).some((group) =>
        getMobileFilterTargetParam(group) === param && hasCheckedFacetInput(group)
      );
      button.classList.toggle('is-active', hasActiveGroup);
    });
  }

  function markMobileFilterTarget(root, targetParam) {
    getFacetGroups(root).forEach((group) => {
      const isTarget = targetParam && !isMergedHiddenFacetGroup(group) && getMobileFilterTargetParam(group) === targetParam;
      group.classList.toggle('is-mobile-filter-target', !!isTarget);
      if (targetParam) {
        group.open = !!isTarget;
      } else if (!isMobileSkippedGroup(group)) {
        group.open = false;
      }
    });
  }

  function setMobileFilterExpandedState(root, targetParam) {
    root.querySelectorAll(mobileFilterOpenSelector).forEach((button) => {
      const buttonParam = normalizeText(button.dataset.tikooMobileFilterParam);
      const expanded = targetParam ? buttonParam === targetParam : !buttonParam;
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function openMobileFilterPanel(root, targetParam = '') {
    if (!isMobileViewport()) return;

    root.classList.add('is-mobile-filter-open');
    if (targetParam) {
      root.dataset.tikooMobileFilterMode = 'direct';
      root.dataset.tikooMobileFilterParam = targetParam;
    } else {
      root.dataset.tikooMobileFilterMode = 'all';
      delete root.dataset.tikooMobileFilterParam;
    }

    markMobileFilterTarget(root, targetParam);
    setMobileFilterExpandedState(root, targetParam);
  }

  function closeMobileFilterPanel(root) {
    root.classList.remove('is-mobile-filter-open');
    delete root.dataset.tikooMobileFilterMode;
    delete root.dataset.tikooMobileFilterParam;
    markMobileFilterTarget(root, '');
    setMobileFilterExpandedState(root, '');
  }

  function syncOpenMobileFilterPanel(root) {
    if (!root.classList.contains('is-mobile-filter-open')) return;
    if (!isMobileViewport()) {
      closeMobileFilterPanel(root);
      return;
    }

    markMobileFilterTarget(root, normalizeText(root.dataset.tikooMobileFilterParam));
  }

  function bindMobileFilterControls(root) {
    if (root.dataset.tikooMobileFilterReady !== 'true') {
      root.dataset.tikooMobileFilterReady = 'true';
      root.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;

        const openButton = target.closest(mobileFilterOpenSelector);
        if (openButton && root.contains(openButton)) {
          event.preventDefault();
          openMobileFilterPanel(root, normalizeText(openButton.dataset.tikooMobileFilterParam));
          return;
        }

        const closeButton = target.closest(mobileFilterCloseSelector);
        if (closeButton && root.contains(closeButton)) {
          event.preventDefault();
          closeMobileFilterPanel(root);
        }
      });

      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && root.classList.contains('is-mobile-filter-open')) {
          closeMobileFilterPanel(root);
        }
      });

      const mobileViewportMediaQuery = window.matchMedia?.(mobileViewportQuery);
      mobileViewportMediaQuery?.addEventListener('change', () => {
        if (!isMobileViewport()) {
          closeMobileFilterPanel(root);
        }
      });
    }

    setMobileFilterButtonState(root);
    syncOpenMobileFilterPanel(root);
  }

  function sortFacetItemsWithOtherLast(items) {
    return [...items].sort((left, right) => {
      const leftIsOther = isOtherFacetLabel(getFacetOptionLabel(left));
      const rightIsOther = isOtherFacetLabel(getFacetOptionLabel(right));
      if (leftIsOther === rightIsOther) return 0;
      return leftIsOther ? 1 : -1;
    });
  }

  function moveOtherOptionsToEnd(list) {
    if (!list) return;
    if (list.dataset.tikooTreeMode === 'true') return;
    sortFacetItemsWithOtherLast(getFacetItems(list)).forEach((item) => list.append(item));
  }

  function readFacetCount(item) {
    const countText = normalizeText(item?.querySelector('.tikoo-facet-option__count')?.textContent || '');
    const parsed = Number.parseInt(countText, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function createGeneratedCategoryItem(label, childItems) {
    // Shopify 在组合筛选后可能只返回子类目；此时补一个可提交的父类目 checkbox。
    const item = document.createElement('li');
    item.className = 'list-menu__item facets__item tikoo-facet-values__item tikoo-facet-values__item--generated-parent';
    item.dataset.tikooFacetOption = 'true';
    item.dataset.tikooFacetLabel = label;
    item.dataset.tikooVintageCategoryValue = label;

    const inputId = `TikooGeneratedVintageCategory-${generatedCategorySequence++}`;
    const currentCategoryValues = getCurrentSearchParams().getAll(vintageCategoryParamName);
    const isActive = currentCategoryValues.some((value) => normalizeFacetKey(value) === normalizeFacetKey(label));
    if (isActive) {
      item.classList.add('is-active');
    }

    const row = document.createElement('label');
    row.className = `facets__label facet-checkbox tikoo-facet-option${isActive ? ' active' : ''}`;
    row.setAttribute('for', inputId);

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = vintageCategoryParamName;
    input.value = label;
    input.id = inputId;
    input.checked = isActive;
    input.dataset.tikooFacetInput = 'true';

    const text = document.createElement('span');
    text.className = 'facet-checkbox__text';
    text.setAttribute('aria-hidden', 'true');

    const labelElement = document.createElement('span');
    labelElement.className = 'facet-checkbox__text-label';
    labelElement.textContent = label;

    const countElement = document.createElement('span');
    countElement.className = 'tikoo-facet-option__count';
    const count = childItems.reduce((total, childItem) => total + readFacetCount(childItem), 0);
    countElement.textContent = count > 0 ? String(count) : '';

    text.append(labelElement, countElement);
    row.append(input, text);
    item.append(row);
    return item;
  }

  function getShowMoreButton(list) {
    return list?.closest('.tikoo-facet-group__fieldset')?.querySelector('[data-tikoo-show-more]');
  }

  function setListExpanded(list, expanded) {
    if (!list) return;
    list.dataset.tikooExpanded = expanded ? 'true' : 'false';
  }

  function applyFacetListLimit(list, forceExpanded = false) {
    if (!list) return;

    const button = getShowMoreButton(list);
    const items = getFacetItems(list);
    const limit = getFacetVisibleLimit(list);
    const searchMode = list.dataset.tikooSearchMode === 'true';
    const treeMode = list.dataset.tikooTreeMode === 'true';
    const expanded = forceExpanded || list.dataset.tikooExpanded === 'true';
    let visibleCount = 0;
    let hiddenCount = 0;
    let collapsibleCount = 0;
    let parentHiddenByLimit = false;

    items.forEach((item) => {
      const isSearchHidden = item.dataset.tikooSearchHidden === 'true';
      const isActive = item.classList.contains('is-active') || item.classList.contains('has-active-child');
      const isChild = item.classList.contains('tikoo-facet-values__item--child');
      const isTreeCollapsed = treeMode && isChild && item.dataset.tikooTreeCollapsed === 'true' && !isActive;
      const shouldCountForLimit = !treeMode || !isChild;
      const shouldHideByLimit = treeMode && isChild
        ? parentHiddenByLimit && !isActive
        : !expanded && !isActive && !isSearchHidden && visibleCount >= limit;
      const shouldHide = isSearchHidden || isTreeCollapsed || shouldHideByLimit;
      item.hidden = shouldHide;

      if (!isSearchHidden) {
        if (shouldCountForLimit && !isActive) {
          collapsibleCount += 1;
          if (shouldHideByLimit) hiddenCount += 1;
          else visibleCount += 1;
        }

        if (treeMode && !isChild) {
          parentHiddenByLimit = shouldHideByLimit;
        }
      }
    });

    if (!button) return;
    if (searchMode) {
      button.hidden = true;
      return;
    }

    button.hidden = collapsibleCount <= limit;
    button.textContent = expanded ? 'Show less' : `Show ${hiddenCount} more`;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }

  function clearSearchHiddenState(list) {
    getFacetItems(list).forEach((item) => {
      item.dataset.tikooSearchHidden = 'false';
    });
  }

  function applyVintageCategoryTree(root = document) {
    root.querySelectorAll('[data-tikoo-facets][data-tikoo-filter-profile="vintage"]').forEach((facetsRoot) => {
      const categoryList = facetsRoot.querySelector(vintageCategoryValuesSelector);
      const detailList = facetsRoot.querySelector(vintageDetailValuesSelector);
      const detailGroup = detailList?.closest('.tikoo-facet-group');
      if (!categoryList || !detailList || !detailGroup) return;

      restoreMergedDetailItems(categoryList, detailList);
      const detailItems = getFacetItems(detailList);
      if (detailItems.length === 0) {
        detailGroup.hidden = true;
        detailGroup.dataset.tikooMergedIntoCategory = 'true';
        applyFacetListLimit(categoryList);
        return;
      }

      const categoryItems = sortFacetItemsWithOtherLast(getFacetItems(categoryList));
      if (categoryItems.length === 0) return;
      categoryList.dataset.tikooTreeMode = 'true';
      categoryItems.forEach((item) => categoryList.append(item));

      const categoryMap = new Map();
      categoryItems.forEach((item) => {
        resetCategoryParentItem(item);
        const key = normalizeFacetKey(item.dataset.tikooVintageCategoryValue || getFacetOptionLabel(item));
        if (key) {
          item.dataset.tikooVintageCategoryKey = key;
          categoryMap.set(key, item);
        }
      });

      const groupedDetailItems = new Map();
      const missingParentLabels = new Map();
      const orphanDetails = [];
      detailItems.forEach((item) => {
        const parentKey = normalizeFacetKey(item.dataset.tikooVintageDetailParent);
        const parentItem = categoryMap.get(parentKey);
        item.classList.add('tikoo-facet-values__item--child');
        item.dataset.tikooVintageDetailParentKey = parentKey;
        setDetailItemDepth(item, 1);

        if (!parentItem) {
          if (parentKey) {
            if (!groupedDetailItems.has(parentKey)) {
              groupedDetailItems.set(parentKey, []);
            }
            if (!missingParentLabels.has(parentKey)) {
              missingParentLabels.set(parentKey, normalizeText(item.dataset.tikooVintageDetailParent));
            }
            groupedDetailItems.get(parentKey).push(item);
          } else {
            orphanDetails.push(item);
          }
          return;
        }

        if (!groupedDetailItems.has(parentKey)) {
          groupedDetailItems.set(parentKey, []);
        }
        groupedDetailItems.get(parentKey).push(item);
      });

      groupedDetailItems.forEach((items, parentKey) => {
        let cursor = categoryMap.get(parentKey);
        if (!cursor) {
          cursor = createGeneratedCategoryItem(missingParentLabels.get(parentKey), items);
          categoryMap.set(parentKey, cursor);
          categoryList.append(cursor);
        }
        cursor.dataset.tikooVintageCategoryKey = parentKey;
        const hasActiveChild = items.some((item) => item.classList.contains('is-active'));
        if (hasActiveChild) {
          cursor.classList.add('has-active-child');
        }
        sortFacetItemsWithOtherLast(items).forEach((item) => {
          cursor.insertAdjacentElement('afterend', item);
          cursor = item;
        });
        setCategoryExpanded(categoryList, categoryMap.get(parentKey), hasActiveChild);
        ensureCategoryToggle(categoryList, categoryMap.get(parentKey), missingParentLabels.get(parentKey) || getFacetOptionLabel(categoryMap.get(parentKey)));
      });

      orphanDetails.forEach((item) => {
        item.classList.remove('tikoo-facet-values__item--child');
        setDetailItemDepth(item, 0);
        categoryList.append(item);
      });
      detailGroup.hidden = true;
      detailGroup.dataset.tikooMergedIntoCategory = 'true';
      applyFacetListLimit(categoryList);
    });
  }

  function setBrandStatus(search, message) {
    const status = search.querySelector(brandStatusSelector);
    if (status) status.textContent = message || '';
  }

  function renderDefaultBrandList(search) {
    const list = getBrandList(search);
    if (list) list.dataset.tikooSearchMode = 'false';
    clearSearchHiddenState(list);
    applyFacetListLimit(list);
  }

  function renderLocalBrandSearch(search, query) {
    const list = getBrandList(search);
    if (list) list.dataset.tikooSearchMode = 'true';
    const normalizedQuery = query.toLowerCase();
    const items = getStaticBrandItems(search);
    let visibleCount = 0;
    items.forEach((item) => {
      const isActive = item.classList.contains('is-active');
      const isMatch = getBrandItemLabel(item).toLowerCase().includes(normalizedQuery);
      const shouldShow = isActive || (isMatch && visibleCount < remoteBrandLimit);
      item.dataset.tikooSearchHidden = shouldShow ? 'false' : 'true';
      if (!isActive && isMatch && shouldShow) visibleCount += 1;
    });
    applyFacetListLimit(list, true);
    return visibleCount;
  }

  function renderRemoteBrands(search, brands) {
    const list = getBrandList(search);
    if (!list) return;

    list.querySelectorAll('[data-tikoo-remote-brand-option]').forEach((item) => item.remove());
    const existingValues = new Set(
      getStaticBrandItems(search).map((item) => getBrandItemLabel(item).toLowerCase())
    );
    const fragment = document.createDocumentFragment();

    brands
      .filter((brand) => normalizeText(brand?.value || brand?.label))
      .forEach((brand) => {
        const label = normalizeText(brand.label || brand.value);
        const value = normalizeText(brand.value || brand.label);
        if (existingValues.has(label.toLowerCase()) || existingValues.has(value.toLowerCase())) return;

        const item = document.createElement('li');
        item.className = 'list-menu__item facets__item tikoo-facet-values__item tikoo-facet-values__item--remote';
        item.dataset.tikooRemoteBrandOption = 'true';
        item.dataset.tikooSearchHidden = 'false';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tikoo-facet-option tikoo-facet-option--remote';
        button.dataset.tikooRemoteBrandValue = value;
        button.innerHTML = `
          <span class="facet-checkbox__text" aria-hidden="true">
            <span class="facet-checkbox__text-label"></span>
            <span class="tikoo-facet-option__count"></span>
          </span>
        `;
        button.querySelector('.facet-checkbox__text-label').textContent = label;
        button.querySelector('.tikoo-facet-option__count').textContent =
          typeof brand.count === 'number' ? String(brand.count) : '';
        item.append(button);
        fragment.append(item);
      });

    list.append(fragment);
    applyFacetListLimit(list, true);
  }

  function bindShowMoreButton(button) {
    if (!button) return;
    if (button.dataset.tikooShowMoreReady === 'true') return;
    button.dataset.tikooShowMoreReady = 'true';
    button.addEventListener('click', () => {
      const list = button.closest('.tikoo-facet-group__fieldset')?.querySelector(facetValuesSelector);
      const expanded = list?.dataset.tikooExpanded === 'true';
      setListExpanded(list, !expanded);
      applyFacetListLimit(list);
    });
  }

  function bindFacetList(list) {
    if (list.closest('.tikoo-facet-group')?.dataset.tikooMergedIntoCategory === 'true') return;
    if (list.dataset.tikooFacetListReady === 'true') {
      moveOtherOptionsToEnd(list);
      applyFacetListLimit(list);
      return;
    }

    list.dataset.tikooFacetListReady = 'true';
    setListExpanded(list, false);
    bindShowMoreButton(getShowMoreButton(list));
    clearSearchHiddenState(list);
    moveOtherOptionsToEnd(list);
    applyFacetListLimit(list);
  }

  function bindBrandSearch(search) {
    if (search.dataset.tikooBrandSearchReady === 'true') return;
    search.dataset.tikooBrandSearchReady = 'true';

    renderDefaultBrandList(search);

    const input = search.querySelector(brandInputSelector);
    if (!input) return;

    let token = 0;
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      event.stopPropagation();
    });

    let debounceTimer = null;
    const clearRemoteBrandOptions = () => {
      search
        .closest('.tikoo-facet-group')
        ?.querySelectorAll('[data-tikoo-remote-brand-option]')
        .forEach((item) => item.remove());
    };

    input.addEventListener('input', (event) => {
      event.stopPropagation();
      const query = normalizeText(input.value);
      token += 1;
      const currentToken = token;
      window.clearTimeout(debounceTimer);

      const localCount = query.length > 0 ? renderLocalBrandSearch(search, query) : 0;
      clearRemoteBrandOptions();

      if (query.length === 0) {
        renderDefaultBrandList(search);
        setBrandStatus(search, '');
        return;
      }

      if (query.length < 2) {
        setBrandStatus(search, localCount > 0 ? '' : 'Type 2+ characters.');
        return;
      }

      const root = search.closest('[data-tikoo-facets]');
      setBrandStatus(search, 'Searching');
      debounceTimer = window.setTimeout(async () => {
        if (currentToken !== token) return;
        try {
          const response = await apiFetch(buildBrandSearchPath(root, query));
          if (currentToken !== token) return;
          const brands = Array.isArray(response?.data?.brands)
            ? response.data.brands
            : Array.isArray(response?.brands)
              ? response.brands
              : [];
          renderRemoteBrands(search, brands);
          setBrandStatus(search, brands.length === 0 && localCount === 0 ? 'No brands found.' : '');
        } catch {
          if (currentToken !== token) return;
          setBrandStatus(search, localCount > 0 ? '' : 'Brand search unavailable.');
        }
      }, 180);
    });
  }

  function bindSingleSelect(form) {
    form.querySelectorAll(singleSelectSelector).forEach((input) => {
      if (input.dataset.tikooSingleSelectReady === 'true') return;
      input.dataset.tikooSingleSelectReady = 'true';
      input.addEventListener('change', () => {
        if (!input.checked) return;
        form
          .querySelectorAll(`input[name="${CSS.escape(input.name)}"]${singleSelectSelector}`)
          .forEach((other) => {
            if (other !== input) other.checked = false;
          });
      });
    });
  }

  function setFacetItemChecked(input, checked) {
    input.checked = checked;
    const item = input.closest('[data-tikoo-facet-option]');
    const label = input.closest('.tikoo-facet-option');
    if (item) item.classList.toggle('is-active', checked);
    if (label) label.classList.toggle('active', checked);
  }

  function bindVintageCategoryExclusion(form) {
    // Category 与 Detail 是同一棵类目树的两种粒度，提交前保持互斥，避免结果被过度收窄。
    if (form.dataset.tikooVintageCategoryExclusionReady === 'true') return;
    form.dataset.tikooVintageCategoryExclusionReady = 'true';

    form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const input = target.closest(facetInputSelector);
      if (!input?.checked) return;

      const changedItem = input.closest('[data-tikoo-facet-option]');
      const changedInCategory = !!changedItem?.dataset.tikooVintageCategoryValue;
      const changedInDetail = !!changedItem?.dataset.tikooVintageDetailValue;
      if (!changedInCategory && !changedInDetail) return;

      const selectorToClear = changedInCategory
        ? `[data-tikoo-vintage-detail-value] ${facetInputSelector}:checked`
        : `[data-tikoo-vintage-category-value] ${facetInputSelector}:checked`;

      form.querySelectorAll(selectorToClear).forEach((other) => {
        if (other !== input) {
          setFacetItemChecked(other, false);
        }
      });
    }, true);
  }

  function bindRemoteBrandOptions(root) {
    if (root.dataset.tikooRemoteBrandReady === 'true') return;
    root.dataset.tikooRemoteBrandReady = 'true';
    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tikoo-remote-brand-value]');
      if (!button) return;
      event.preventDefault();
      updateUrlWithBrand(root, normalizeText(button.dataset.tikooRemoteBrandValue));
    });
  }

  function syncAppliedFacetsState(root = document) {
    root.querySelectorAll('.tikoo-applied-facets').forEach((appliedFacets) => {
      const hasActiveFilters = !!appliedFacets.querySelector('.tikoo-applied-facets__chips facet-remove');
      appliedFacets.hidden = !hasActiveFilters;
      if (hasActiveFilters) {
        appliedFacets.open = true;
      }
    });
  }

  function initialize(root = document) {
    syncAppliedFacetsState(root);
    applyVintageCategoryTree(root);
    root.querySelectorAll(facetValuesSelector).forEach(bindFacetList);
    root.querySelectorAll(brandSearchSelector).forEach(bindBrandSearch);
    root.querySelectorAll('facet-filters-form form').forEach(bindVintageCategoryExclusion);
    root.querySelectorAll('facet-filters-form form').forEach(bindSingleSelect);
    root.querySelectorAll('[data-tikoo-facets]').forEach(bindRemoteBrandOptions);
    root.querySelectorAll('[data-tikoo-facets]').forEach(bindMobileFilterControls);
  }

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('tikoo:facets-rendered', (event) => initialize(event.target || document));
})();
